/**
 * Utility function to retry an async operation with exponential backoff
 */
export async function retryWithBackoff<T>(
  operation: () => Promise<T>,
  maxAttempts: number = 3,
  baseDelay: number = 1000,
): Promise<T> {
  let lastError: Error;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error as Error;

      if (attempt === maxAttempts) {
        throw lastError;
      }

      // Exponential backoff: 1s, 2s, 4s, etc.
      const delay = baseDelay * Math.pow(2, attempt - 1);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  throw lastError!;
}

/**
 * Type guard to validate chat bot hash response
 */
export function isValidChatBotHashResponse(data: unknown): data is { chatbot_hash: string } {
  return (
    typeof data === 'object' &&
    data !== null &&
    'chatbot_hash' in data &&
    typeof (data as Record<string, unknown>).chatbot_hash === 'string' &&
    ((data as Record<string, unknown>).chatbot_hash as string).length > 0
  );
}
